import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarPlot,
  ChartsAxisHighlight,
  ChartsGrid,
  ChartsTooltip,
  ChartsXAxis,
  ChartsYAxis,
  LinePlot,
  MarkPlot,
  ResponsiveChartContainer,
} from '@mui/x-charts';
import { FleetDataMissions } from 'models/fleet-management/FleetDataSet';
import { numberFormatter } from 'utils/numbers';
import { DistanceValueUnit } from '@common/enums/configuration/units';
import useDistanceUnitValue from '@common/hooks/useDistanceUnitValue';
import {
  BLUE_RAMP,
  WEEKLY_CHART_MARGIN,
  chartSx,
  weeklyTickLabelStyle,
} from 'utils/fleet-dashboard-theme';

const CHART_HEIGHT = 300;
const AREA_AXIS = 'area';
const HOURS_AXIS = 'hours';

/**
 * Area and hours share the week axis, each on its own y scale: area on the left as bars, hours on
 * the right as a line. The mark tells the two apart as much as the shade does; there is no legend,
 * the tooltip names both values of the hovered week.
 */
const COLORS = { area: BLUE_RAMP[1], hours: BLUE_RAMP[4] };

/** Room for the second y axis on the right. */
const MARGIN = { ...WEEKLY_CHART_MARGIN, right: 56 };

const WeeklyOutputChart: React.FC<{ missionsData: FleetDataMissions[] }> = ({ missionsData }) => {
  const { t, i18n } = useTranslation();
  const { multiplier, label: areaUnit } = useDistanceUnitValue(1, DistanceValueUnit.METERS_SQUARED);
  const hoursUnit = t('time-unit-hours');

  const rows = useMemo(
    () =>
      (missionsData ?? []).map((w) => ({
        weekLabel: new Date(w.week_start).toLocaleDateString(i18n.language, {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        }),
        area: (w.total_area ?? 0) * multiplier,
        hours: w.total_duration ?? 0,
      })),
    [i18n.language, missionsData, multiplier],
  );

  const number = (value: number, digits = 0) =>
    value.toLocaleString(i18n.language, { maximumFractionDigits: digits });

  return (
    <ResponsiveChartContainer
      dataset={rows}
      height={CHART_HEIGHT}
      margin={MARGIN}
      xAxis={[{ dataKey: 'weekLabel', scaleType: 'band', tickLabelStyle: weeklyTickLabelStyle }]}
      yAxis={[
        // A fleet's weekly totals run into the thousands, so the ticks are abbreviated (k, M) to
        // stay inside the margins; the tooltip keeps the full value.
        { id: AREA_AXIS, valueFormatter: (v: number) => `${numberFormatter(v, 1)} ${areaUnit}` },
        { id: HOURS_AXIS, valueFormatter: (v: number) => `${numberFormatter(v, 1)} ${hoursUnit}` },
      ]}
      series={[
        {
          type: 'bar',
          dataKey: 'area',
          yAxisId: AREA_AXIS,
          label: t('fleet-management-detail-area-per-week', { unit: areaUnit }),
          color: COLORS.area,
          valueFormatter: (v) => `${number(v ?? 0)} ${areaUnit}`,
        },
        {
          type: 'line',
          dataKey: 'hours',
          yAxisId: HOURS_AXIS,
          label: t('fleet-management-field-total_duration'),
          color: COLORS.hours,
          showMark: true,
          valueFormatter: (v) => `${number(v ?? 0, 1)} ${hoursUnit}`,
        },
      ]}
      sx={chartSx}
    >
      <ChartsGrid horizontal />
      <BarPlot borderRadius={4} />
      <LinePlot />
      <MarkPlot />
      <ChartsXAxis position="bottom" />
      <ChartsYAxis axisId={AREA_AXIS} position="left" />
      <ChartsYAxis axisId={HOURS_AXIS} position="right" />
      <ChartsAxisHighlight x="band" />
      {/* Axis trigger: hovering a week lists both its values. */}
      <ChartsTooltip trigger="axis" />
    </ResponsiveChartContainer>
  );
};

export default WeeklyOutputChart;
